/**
 * Date utility functions for consistent date formatting across the app.
 *
 * LOCALIZATION RULE: every function here that produces a string a USER READS
 * formats through `displayLocale()` (the active UI language), never a
 * hardcoded 'en-US'. Functions that produce a MACHINE value — a storage key,
 * a comparison operand, an ISO date — must stay locale-independent:
 * `getLocalDateString`, `timeStringToMinutes`, `minutesToTimeString`,
 * `calculateEndTime`, `parseDurationToMinutes`, `parseLocalDate`,
 * `isValidDateRange` are all in that category and deliberately do no
 * locale formatting. (Related trap outside this file: RecapNudge.js uses
 * `toLocaleDateString('en-CA')` to get a YYYY-MM-DD *key* — that is not a
 * display call and must not be localized.)
 */
import i18n from '../i18n';

// Active UI language for display formatting. Resolved per call — never
// snapshotted at module load, which would freeze the language (docs/I18N.md).
const displayLocale = () => i18n.language || 'en';

/**
 * The locale to pass to any `toLocale*` DISPLAY call outside this module.
 * Exported so components format in the app's active language rather than a
 * hardcoded 'en-US' (or the browser default, which can disagree with the
 * user's in-app choice).
 *
 * Do NOT use for machine values — a YYYY-MM-DD storage key or a string used
 * in a comparison must stay locale-independent (use `getLocalDateString`).
 * @returns {string} BCP-47 language tag for the active UI language
 */
export const getDisplayLocale = () => displayLocale();

/**
 * Weekday abbreviations for calendar column headers, Sunday-first, in the
 * active language. en → ["Sun","Mon",…], nl → ["zo","ma",…].
 * Replaces the hardcoded English arrays that used to live in each calendar
 * component. Call this per render — never hoist the result to module scope,
 * which would freeze the language.
 * @param {'short'|'long'|'narrow'} [format]
 * @returns {string[]} 7 labels, index 0 = Sunday
 */
export const getWeekdayLabels = (format = 'short') => {
  // 2023-01-01 was a Sunday — anchors the array to a Sunday-first week.
  return Array.from({ length: 7 }, (_, i) =>
    new Date(2023, 0, 1 + i).toLocaleDateString(displayLocale(), { weekday: format })
  );
};

/**
 * Month names in the active language, January-first.
 * en → ["Jan","Feb",…], nl → ["jan","feb","mrt",…]. Same no-hoisting rule as
 * getWeekdayLabels.
 * @param {'short'|'long'|'narrow'} [format]
 * @returns {string[]} 12 labels, index 0 = January
 */
export const getMonthLabels = (format = 'short') => {
  return Array.from({ length: 12 }, (_, i) =>
    new Date(2023, i, 1).toLocaleDateString(displayLocale(), { month: format })
  );
};

/**
 * Format an "MM-DD" value (a season boundary — no year available) for display.
 * Uses a full locale format rather than "<month> <day>" concatenation, so the
 * day/month ORDER follows the language: en → "May 15", nl → "15 mei".
 * @param {string} mmdd - "MM-DD"
 * @returns {string|null} Localized date, the input when unparseable, null when empty
 */
export const formatMonthDay = (mmdd) => {
  if (!mmdd) return null;
  const [mm, dd] = String(mmdd).split('-').map(Number);
  if (!mm || mm < 1 || mm > 12 || !dd) return mmdd;
  // 2000 is a leap year, so 02-29 formats correctly.
  return new Date(2000, mm - 1, dd).toLocaleDateString(displayLocale(), {
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Compact number formatting for counts: 1234 → "1.2K" (en) / "1,2K" (nl).
 * Uses Intl compact notation, which also fixes the old hand-rolled version's
 * breakdown above a million ("1200K" → "1.2M").
 * @param {number} n
 * @returns {string} Formatted count ('' when falsy)
 */
export const formatCompactCount = (n) => {
  if (!n) return '';
  try {
    return new Intl.NumberFormat(displayLocale(), {
      notation: 'compact',
      compactDisplay: 'short',
      maximumFractionDigits: 1
    }).format(n);
  } catch (e) {
    return String(n);
  }
};

/**
 * Safely parse a date string into a Date object without timezone shifting.
 * Date-only strings like "2025-03-13" are parsed as UTC midnight by the JS spec,
 * which shifts to the previous day in western timezones (e.g., PST shows March 12).
 * This function appends T12:00:00 to date-only strings so they parse as local noon.
 * @param {string|Date} input - ISO date string or Date object
 * @returns {Date} Date object safe for local display
 */
export const parseLocalDate = (input) => {
  if (input instanceof Date) return input;
  if (!input) return new Date(NaN);
  // Date-only format: YYYY-MM-DD (exactly 10 chars, no 'T')
  if (typeof input === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(input)) {
    return new Date(input + 'T12:00:00');
  }
  return new Date(input);
};

/**
 * Whether an event is over: true only once the viewer's local day of its last
 * date (endDate, else eventDate) has ended. A show stays live all of its day,
 * and a multi-day run until its end date. Never `new Date('YYYY-MM-DD') < now`:
 * that is UTC midnight, so a show read as past from the evening before in the
 * Americas (BH-08-08). No date, or an unparseable one, is never over.
 * @param {string|Date|null} eventDate
 * @param {string|Date|null} [endDate]
 * @param {Date} [now]
 * @returns {boolean}
 */
export const isEventOver = (eventDate, endDate = null, now = new Date()) => {
  const last = parseLocalDate(endDate || eventDate);
  if (Number.isNaN(last.getTime())) return false;
  const nextLocalMidnight = new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1);
  return now >= nextLocalMidnight;
};

/**
 * Format a date string into a readable format
 * @param {string} dateString - ISO date string
 * @param {object} options - Formatting options
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString, options = {}) => {
  // Defensive check for undefined/null dates
  if (!dateString) {
    return i18n.t('labels:dateUtils.unknownDate');
  }

  const defaultOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...options
  };

  // Parse the date (handles ISO format from backend)
  const date = parseLocalDate(dateString);

  // Check if date is valid
  if (isNaN(date.getTime())) {
    console.warn('Invalid date string:', dateString);
    return i18n.t('labels:dateUtils.invalidDate');
  }

  return date.toLocaleDateString(displayLocale(), defaultOptions);
};

/**
 * Format a date for display in trip headers (e.g., "June 16th")
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date string
 */
export const formatTripDate = (dateString) => {
  return formatDate(dateString, { month: 'long', day: 'numeric' });
};

/**
 * Format a date range for display
 * @param {string} startDate - ISO start date string
 * @param {string} endDate - ISO end date string
 * @returns {string} Formatted date range string
 */
export const formatDateRange = (startDate, endDate) => {
  const start = formatDate(startDate);
  const end = formatDate(endDate);
  return `${start} - ${end}`;
};

/**
 * Format a date with weekday for planner display
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date string with weekday
 */
export const formatDateWithWeekday = (dateString) => {
  return formatDate(dateString, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

/**
 * Format a date as a relative time string (e.g., "2h ago", "3d ago")
 * @param {string} dateString - ISO date string
 * @returns {string} Relative time string
 */
export const formatTimeAgo = (dateString) => {
  if (!dateString) return i18n.t('labels:dateUtils.recently');

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return i18n.t('labels:dateUtils.recently');

  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);

  if (diffMins < 1) return i18n.t('labels:dateUtils.justNow');

  // Intl.RelativeTimeFormat with numeric:'always' + style:'narrow' reproduces
  // the previous hand-built English EXACTLY ("5m ago", "2h ago", "3d ago",
  // "2w ago", "5mo ago") while giving every other locale its own forms.
  const rel = (value, unit) => {
    try {
      return new Intl.RelativeTimeFormat(displayLocale(), { numeric: 'always', style: 'narrow' })
        .format(-value, unit);
    } catch (e) {
      return date.toLocaleDateString(displayLocale());
    }
  };

  if (diffMins < 60) return rel(diffMins, 'minute');
  if (diffHours < 24) return rel(diffHours, 'hour');
  if (diffDays < 7) return rel(diffDays, 'day');
  if (diffDays < 30) return rel(diffWeeks, 'week');
  if (diffMonths < 12) return rel(diffMonths, 'month');
  return date.toLocaleDateString(displayLocale());
};

/**
 * Format a visit date respecting its stored precision
 * (activities.visit_date_precision: 'exact' | 'month' | 'year' | 'unknown').
 * Rendering a month/year-precision date as a full day would fabricate
 * specificity the user never gave.
 * exact → "Jun 12, 2023", month → "Jun 2023", year → "2023", unknown → ''
 * @param {string} dateString - ISO date string (visit_date)
 * @param {string} precision - Stored precision bucket
 * @returns {string} Formatted date string ('' when missing/unknown)
 */
export const formatVisitDate = (dateString, precision = 'exact') => {
  if (!dateString || precision === 'unknown') return '';
  const date = parseLocalDate(dateString);
  if (isNaN(date.getTime())) return '';
  if (precision === 'year') return String(date.getFullYear());
  if (precision === 'month') return date.toLocaleDateString(displayLocale(), { month: 'short', year: 'numeric' });
  return date.toLocaleDateString(displayLocale(), { month: 'short', day: 'numeric', year: 'numeric' });
};

/**
 * Card-label variant of formatVisitDate: fresh exact visits keep the lively
 * relative form ("2d ago"), anything older or fuzzier gets the precision-aware
 * absolute date. Use on memory/visit cards where visit_date may be backdated —
 * formatTimeAgo(completed_at) there reads "just now" for a years-old visit.
 * @param {string} dateString - ISO date string (visit_date)
 * @param {string} precision - Stored precision bucket
 * @returns {string} Formatted label ('' when missing/invalid)
 */
export const formatVisitDateLabel = (dateString, precision = 'exact') => {
  if (!dateString) return '';
  if (precision !== 'exact') return formatVisitDate(dateString, precision);
  const date = parseLocalDate(dateString);
  if (isNaN(date.getTime())) return '';
  const diffDays = Math.floor((Date.now() - date.getTime()) / 86400000);
  if (diffDays < 30) return formatTimeAgo(dateString);
  return formatVisitDate(dateString, precision);
};

/**
 * Format a wall-clock time string for display in the active locale.
 * The 12-vs-24-hour choice is a property of the LOCALE, not a preference we
 * hardcode: en → "2:30 PM", nl → "14:30".
 * @param {string} timeString - Time in HH:MM format (24-hour, as stored)
 * @returns {string} Localized time string
 */
export const formatTime = (timeString) => {
  // Type guard included: a non-string truthy value must return '' rather
  // than throw on .split mid-render.
  if (!timeString || typeof timeString !== 'string') return '';
  const [hours, minutes] = timeString.split(':');
  const hour = parseInt(hours, 10);
  const minute = parseInt(minutes, 10);
  if (isNaN(hour) || isNaN(minute)) return '';
  // Anchor on an arbitrary date — only the time portion is rendered.
  // timeStyle:'short' uses each locale's PREFERRED pattern (en "2:30 PM",
  // nl "09:05" — leading zero included), where hour:'numeric' would drop
  // the zero Dutch convention expects.
  const d = new Date(2000, 0, 1, hour, minute);
  try {
    return new Intl.DateTimeFormat(displayLocale(), { timeStyle: 'short' }).format(d);
  } catch (e) {
    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  }
};

/**
 * Get local date string in YYYY-MM-DD format (avoids UTC conversion issues)
 * @param {Date} date - JavaScript Date object
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const getLocalDateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Is a date-only day (YYYY-MM-DD, as event dates arrive) today or later in the
 * viewer's own calendar? Compares strings, never `new Date(dateStr)`: a
 * date-only string parses as UTC midnight, which west of UTC is the previous
 * evening, so today's show read as past all day in Los Angeles (BH-07-02).
 * @param {string} dateStr - YYYY-MM-DD (a longer ISO string is read by its date part)
 * @param {Date} [now] - defaults to the current time
 * @returns {boolean} false for a missing date
 */
export const isLocalTodayOrLater = (dateStr, now = new Date()) => {
  const day = String(dateStr || '').slice(0, 10);
  return !!day && day >= getLocalDateString(now);
};

/**
 * Shift a YYYY-MM-DD string by n calendar days (n may be negative).
 * Noon-anchored so a DST transition can't move the result across midnight
 * — the same pattern previously hand-rolled at every call site.
 * @param {string} dateStr - YYYY-MM-DD
 * @param {number} n - days to add
 * @returns {string} YYYY-MM-DD
 */
export const addDays = (dateStr, n) => {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + n);
  return getLocalDateString(d);
};

/**
 * Format a date range with abbreviated months (used in headers)
 * @param {string|Date} startInput - Start date (ISO string or Date object)
 * @param {string|Date} endInput - End date (ISO string or Date object)
 * @returns {string} Formatted date range (e.g., "Jun 16 - Jun 23"); a
 *   same-day range is that one day, "Jun 16", never "Jun 16-16" (C321)
 */
export const formatDateRangeShort = (startInput, endInput) => {
  if (!startInput || !endInput) return i18n.t('labels:dateUtils.unknownDates');

  const start = parseLocalDate(startInput);
  const end = parseLocalDate(endInput);

  if (isNaN(start.getTime()) || isNaN(end.getTime())) return i18n.t('labels:dateUtils.invalidDates');

  const options = { month: 'short', day: 'numeric' };
  const startStr = start.toLocaleDateString(displayLocale(), options);

  if (start.getFullYear() === end.getFullYear()
    && start.getMonth() === end.getMonth()
    && start.getDate() === end.getDate()) {
    return startStr;
  }

  const endStr = end.toLocaleDateString(displayLocale(), options);

  // If same month, show "Jun 16-23"
  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    const month = start.toLocaleDateString(displayLocale(), { month: 'short' });
    return `${month} ${start.getDate()}-${end.getDate()}`;
  }

  return `${startStr} - ${endStr}`;
};

/**
 * Format a date as short format without year (e.g., "Jan 15")
 * @param {string|Date} dateInput - ISO date string or Date object
 * @returns {string} Formatted date string
 */
export const formatDateShort = (dateInput) => {
  if (!dateInput) return i18n.t('labels:dateUtils.unknownDate');
  const date = parseLocalDate(dateInput);
  if (isNaN(date.getTime())) return i18n.t('labels:dateUtils.invalidDate');
  return date.toLocaleDateString(displayLocale(), { month: 'short', day: 'numeric' });
};

/**
 * Format a date with weekday short form (e.g., "Mon, Jan 15")
 * @param {string|Date} dateInput - ISO date string or Date object
 * @returns {string} Formatted date string
 */
export const formatDateWithWeekdayShort = (dateInput, { yearWhenNotThisYear = false } = {}) => {
  if (!dateInput) return i18n.t('labels:dateUtils.unknownDate');
  const date = parseLocalDate(dateInput);
  if (isNaN(date.getTime())) return i18n.t('labels:dateUtils.invalidDate');
  // Opt-in year. Most callers show dates inside a trip, where the year is
  // obvious from context and would be noise — so the default is unchanged.
  // Surfaces listing a long forward horizon need it: on a page running 16
  // months out, "Sun, Jan 3" is genuinely ambiguous between this January and
  // the next.
  const opts = { weekday: 'short', month: 'short', day: 'numeric' };
  if (yearWhenNotThisYear && date.getFullYear() !== new Date().getFullYear()) {
    opts.year = 'numeric';
  }
  return date.toLocaleDateString(displayLocale(), opts);
};

/**
 * Weekday and day of the month, e.g. "Thu 29" (nl "do 29") — inside a trip,
 * where the month is obvious and a button needs the room. Built from parts:
 * en-US's own { weekday, day } order is "29 Thu".
 * @param {string|Date} dateInput - ISO date string or Date object
 * @returns {string} Formatted date string
 */
export const formatWeekdayDay = (dateInput) => {
  if (!dateInput) return i18n.t('labels:dateUtils.unknownDate');
  const date = parseLocalDate(dateInput);
  if (isNaN(date.getTime())) return i18n.t('labels:dateUtils.invalidDate');
  return `${date.toLocaleDateString(displayLocale(), { weekday: 'short' })} ${date.getDate()}`;
};

/**
 * Format a date showing only month and year (e.g., "January 2024")
 * @param {string|Date} dateInput - ISO date string or Date object
 * @returns {string} Formatted date string
 */
export const formatMonthYear = (dateInput) => {
  if (!dateInput) return i18n.t('labels:dateUtils.unknownDate');
  const date = parseLocalDate(dateInput);
  if (isNaN(date.getTime())) return i18n.t('labels:dateUtils.invalidDate');
  return date.toLocaleDateString(displayLocale(), { month: 'long', year: 'numeric' });
};

/**
 * Format a date showing short month and year (e.g., "Oct 2024") — for
 * compact single-line résumés where the long month crowds its neighbours.
 * @param {string|Date} dateInput - ISO date string or Date object
 * @returns {string} Formatted date string
 */
export const formatMonthYearShort = (dateInput) => {
  if (!dateInput) return i18n.t('labels:dateUtils.unknownDate');
  const date = parseLocalDate(dateInput);
  if (isNaN(date.getTime())) return i18n.t('labels:dateUtils.invalidDate');
  return date.toLocaleDateString(displayLocale(), { month: 'short', year: 'numeric' });
};

/**
 * Get weekday name from a date
 * @param {string|Date} dateInput - ISO date string or Date object
 * @param {string} format - 'short' for "Mon" or 'long' for "Monday"
 * @returns {string} Weekday name
 */
export const getWeekday = (dateInput, format = 'short') => {
  if (!dateInput) return '';
  const date = parseLocalDate(dateInput);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString(displayLocale(), { weekday: format });
};

/**
 * Get month name from a date
 * @param {string|Date} dateInput - ISO date string or Date object
 * @param {string} format - 'short' for "Jan" or 'long' for "January"
 * @returns {string} Month name
 */
export const getMonth = (dateInput, format = 'short') => {
  if (!dateInput) return '';
  const date = parseLocalDate(dateInput);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString(displayLocale(), { month: format });
};

/**
 * Get a quick date range based on type.
 *
 * Shared by the search criteria pills/expanded panel and Home search — fix the
 * helper, never fork it, or "this weekend" comes to mean different things on
 * different surfaces.
 *
 * @param {string} type - 'today', 'tomorrow', 'weekend', 'nextWeekend', or '7days'
 * @returns {object} { startDate: string, endDate: string } in YYYY-MM-DD format
 */
export const getQuickDateRange = (type, today = new Date()) => {
  let start, end;

  switch (type) {
    case 'today':
      start = new Date(today);
      end = new Date(today);
      break;
    case 'tomorrow':
      start = new Date(today);
      start.setDate(today.getDate() + 1);
      end = new Date(start);
      break;
    case 'weekend': {
      // The weekend is Friday–Sunday, clipped to "from now" — Friday night is
      // prime for local events, so it counts as the weekend on every day of
      // the week, not only once it is already Friday (decided 2026-08-23).
      //
      //   Mon–Thu → the coming Fri–Sun
      //   Fri     → Fri–Sun     Sat → Sat–Sun     Sun → Sun only
      //
      // Clipped, never slid: while the weekend is still running, "this
      // weekend" must mean the one you are in. The previous logic
      // (`(6 - dow + 7) % 7 || 7`) returned NEXT Saturday when asked on a
      // Saturday or Sunday, which is peak traffic for the question.
      const dow = today.getDay();            // 0 = Sunday … 6 = Saturday
      const alreadyInWeekend = dow === 0 || dow >= 5;
      start = new Date(today);
      if (!alreadyInWeekend) start.setDate(today.getDate() + (5 - dow)); // → Friday
      end = new Date(start);
      // Inside the weekend, run to the Sunday we are heading for (0 if today
      // IS Sunday). Outside it, Friday + 2.
      end.setDate(start.getDate() + (alreadyInWeekend ? (7 - dow) % 7 : 2));
      break;
    }
    case 'nextWeekend': {
      // The Fri–Sun AFTER the one 'weekend' returns. Derived from that Sunday
      // rather than computed independently, so the two can never overlap or
      // leave a gap however the weekend rule changes: Sunday + 5 is always the
      // next Friday, + 7 always the next Sunday.
      //
      // This band exists for lead time. "This weekend" is often already too
      // late to book anything worth booking, which is the whole point of
      // showing the one after it.
      const dow = today.getDay();
      const alreadyInWeekend = dow === 0 || dow >= 5;
      const thisWeekendStart = new Date(today);
      if (!alreadyInWeekend) thisWeekendStart.setDate(today.getDate() + (5 - dow));
      const thisWeekendEnd = new Date(thisWeekendStart);
      thisWeekendEnd.setDate(thisWeekendStart.getDate() + (alreadyInWeekend ? (7 - dow) % 7 : 2));

      start = new Date(thisWeekendEnd);
      start.setDate(thisWeekendEnd.getDate() + 5);
      end = new Date(thisWeekendEnd);
      end.setDate(thisWeekendEnd.getDate() + 7);
      break;
    }
    case '7days':
      start = new Date(today);
      end = new Date(today);
      end.setDate(today.getDate() + 7);
      break;
    default:
      return { startDate: '', endDate: '' };
  }

  return {
    startDate: getLocalDateString(start),
    endDate: getLocalDateString(end)
  };
};

/**
 * Validate that end date is not before start date
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @returns {boolean} True if valid (endDate >= startDate or either is empty)
 */
export const isValidDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return true;
  return parseLocalDate(endDate) >= parseLocalDate(startDate);
};

/**
 * Format a duration in minutes to a human-readable string
 * - Under 60 minutes: "30m"
 * - 60 minutes or more: "1hr", "1.5hr", "2hr"
 * @param {number} minutes - Duration in minutes
 * @returns {string} Formatted duration string
 */
export const formatDuration = (minutes) => {
  if (!minutes || minutes <= 0) return '';

  if (minutes < 60) {
    return i18n.t('labels:dateUtils.duration.minutesCompact', { count: minutes });
  }

  const hours = minutes / 60;
  // Check if it's a whole number
  if (hours === Math.floor(hours)) {
    return i18n.t('labels:dateUtils.duration.hoursCompact', { count: hours });
  }
  // Round to 1 decimal place for cleaner display
  return i18n.t('labels:dateUtils.duration.hoursCompact', { count: Math.round(hours * 10) / 10 });
};

/**
 * Format a duration in minutes as "2h 30m" / "2h" / "45 min".
 * Sibling of formatDuration (which renders the compact "2.5hr" shape) —
 * use this one on itinerary/agenda surfaces that show hours and minutes.
 * @param {number} minutes - Duration in minutes
 * @returns {string} Formatted duration string ('' when missing/zero)
 */
export const formatDurationHM = (minutes) => {
  const mins = parseInt(minutes, 10);
  if (!mins || isNaN(mins) || mins <= 0) return '';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h && m) return i18n.t('labels:dateUtils.duration.hoursMinutes', { hours: h, minutes: m });
  if (h) return i18n.t('labels:dateUtils.duration.hoursOnly', { count: h });
  return i18n.t('labels:dateUtils.duration.minutesOnly', { count: m });
};

/**
 * Convert a "HH:MM" time string to minutes-of-day.
 * @param {string} timeString - Time in HH:MM format (24-hour)
 * @returns {number} Minutes since midnight (0 when missing)
 */
export const timeStringToMinutes = (timeString) => {
  if (!timeString) return 0;
  const [hours, minutes] = timeString.split(':').map(Number);
  return hours * 60 + (minutes || 0);
};

/**
 * Convert minutes-of-day to a "HH:MM" string. Wraps past midnight so an
 * end time like 25:00 formats as 01:00 instead of garbage.
 * @param {number} minutes - Minutes since midnight
 * @returns {string} Time in HH:MM format (24-hour)
 */
export const minutesToTimeString = (minutes) => {
  const h = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

/**
 * Parse a duration of any stored shape into minutes.
 *
 * Re-exported from `./durationParse`, which is the ONE implementation shared
 * with the backend (CommonJS so both runtimes consume the same file — see the
 * header there). This alias exists so the long-standing
 * `import { parseDurationToMinutes } from '.../dateUtils'` call sites keep
 * working; new code may import from either path.
 *
 * Do not re-implement locally — per-surface variants of this parser drifted
 * apart twice before it was consolidated.
 */
export { parseDurationToMinutes } from './durationParse';

/**
 * Compute an end time string from a start time and duration.
 * @param {string} startTime - Time in HH:MM format (24-hour)
 * @param {number} durationMinutes - Duration in minutes
 * @returns {string|null} End time in HH:MM (wrapped past midnight), or null when inputs missing
 */
export const calculateEndTime = (startTime, durationMinutes) => {
  if (!startTime || !durationMinutes) return null;
  return minutesToTimeString(timeStringToMinutes(startTime) + parseInt(durationMinutes, 10));
};

/**
 * Format a hotel stay as "start-lastNight, checkout checkoutDate" (e.g. "Oct 15-Oct 17, checkout Oct 18").
 * lastNight is nights-1 days after start; checkout is nights days after start.
 * @param {string} startDate - Check-in date in YYYY-MM-DD format
 * @param {number} nights - Number of nights stayed
 * @returns {string} Formatted stay range
 */
export const formatHotelStayRange = (startDate, nights) => {
  const start = parseLocalDate(startDate);
  const lastNight = parseLocalDate(startDate);
  lastNight.setDate(lastNight.getDate() + nights - 1);
  const checkout = parseLocalDate(startDate);
  checkout.setDate(checkout.getDate() + nights);

  // Was `undefined` (= browser locale), which could disagree with the app's
  // active language; pin to displayLocale() like every other display call.
  const options = { month: 'short', day: 'numeric' };
  const startStr = start.toLocaleDateString(displayLocale(), options);
  const lastNightStr = lastNight.toLocaleDateString(displayLocale(), options);
  const checkoutStr = checkout.toLocaleDateString(displayLocale(), options);
  // Whole-sentence key — the "checkout" label and the separators around the
  // dates both move in translation.
  return i18n.t('labels:dateUtils.hotelStayRange', {
    start: startStr,
    lastNight: lastNightStr,
    checkout: checkoutStr
  });
};